import { useState, useCallback } from 'react'
import moment from 'moment'

import { callAuthorizedGraphQLEndpoint } from '../../../../../Module/graphQL'
import {
  getConversionLogs,
  getStatistics,
  getMetrics,
  downloadConversions,
} from '../gql'
import { CONVERSION_REPORT_TYPE } from '../../../../../shared/constants'
import { CONVERSIONS_FETCH_STEP } from '../../../../../constants'

const cache = {}
export const useConversionReportsRequest = (cache => campaignId => {
  const [isLoadGetConversionReports, setIsLoadGetConversionReports] = useState(
    false,
  )

  const getConversionReports = useCallback(
    async (args, options = {}) => {
      setIsLoadGetConversionReports(true)
      const key = JSON.stringify(args)

      if (!options.isFresh && cache[campaignId + key]) {
        return cache[campaignId + key]
      }

      const response = await callAuthorizedGraphQLEndpoint(getConversionLogs, {
        campaignId,
        ...args,
      })

      const result = (await response.json()).data.conversionLogs
      setIsLoadGetConversionReports(false)

      return (cache[campaignId + key] = {
        conversionLogs: result.edges.map(({ node }) => node),
        totalCount: result.totalCount,
      })
    },
    [campaignId, cache],
  )

  const getConversionsStatistics = useCallback(async () => {
    const response = await callAuthorizedGraphQLEndpoint(getStatistics, {
      campaignId,
    })

    const result = (await response.json()).data.conversionsStatistics
    return {
      ...result,
    }
  }, [campaignId])

  const getConversionsData = useCallback(
    async ({ conversionLogId, reportType }) => {
      const isWithTimestamp =
        reportType === CONVERSION_REPORT_TYPE.WITH_TIMESTAMP

      const res = await callAuthorizedGraphQLEndpoint(getMetrics, {
        campaignId,
      })
      const metrics = (await res.json()).data.campaign.metrics.nodes.map(
        metric => (metric.label ? metric.label : metric.name),
      )
      const arr = new Array(metrics.length).fill(0)

      const data = []
      const mapPos = {}

      if (isWithTimestamp) {
        data.push(['Flyer code', 'Metric', 'Timestamp'])
      } else {
        data.push(['Flyer code', ...metrics.map(metric => metric)])
      }

      const getPosition = flyerCode => {
        if (!mapPos[flyerCode]) {
          mapPos[flyerCode] = data.length
          data.push([flyerCode, ...arr])
        }
        return mapPos[flyerCode]
      }

      let skip = 0
      while (true) {
        const response = await callAuthorizedGraphQLEndpoint(
          downloadConversions,
          {
            campaignId,
            conversionLogId,
            reportType,
            first: CONVERSIONS_FETCH_STEP,
            skip,
          },
        )
        const result = (await response.json()).data.downloadConversions
        const {
          edges,
          pageInfo: { hasNextPage },
        } = result
        edges.forEach(({ node }) => {
          const strs = node.split(',')
          const flyerCode = strs[0]
          const metricName = strs[1]
          if (isWithTimestamp) {
            data.push([
              flyerCode,
              metricName,
              isNaN(strs[2]) ? moment(strs[2]).unix() : strs[2],
            ])
          } else {
            const pos = getPosition(flyerCode)
            const id = metrics.findIndex(metric => metric === metricName)
            if (id !== -1) data[pos][id + 1] = Number(strs[2])
          }
        })
        if (!hasNextPage) break
        skip += CONVERSIONS_FETCH_STEP
      }
      return data
    },
    [campaignId],
  )

  return {
    getConversionReports,
    getConversionsStatistics,
    getConversionsData,
    isLoadGetConversionReports,
  }
})(cache)
